import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Big } from "big.js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { CostType, Stage } from "../../shared/CaseConfiguration";
import {
  NOTHING_LOCKED,
  plannedAt,
  withSpentAt,
} from "../../shared/costChanges";
import { Budget, LockedCosts } from "../../shared/CostModel";
import { endDateRange, IsoDate, showDate, withEnded } from "../../shared/timeline";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { AsExpectedButton } from "./AsExpectedButton";

const button = {
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;
const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;

/** What closing shows of a party: its open costs of the stage by type. */
export type PartyCosts = {
  partyId: string;
  name: string;
  budget: Budget;
  locked: LockedCosts;
  rows: { type: CostType; planned: Big; spent: Big }[];
};

/**
 * An amount as the user types it; undefined where it is not one. Below
 * nothing only where `negative` allows it: a reserve can be, what was spent
 * cannot.
 */
export const amountOf = (
  text: string,
  { negative = false }: { negative?: boolean } = {},
): Big | undefined => {
  const cleaned = text.replace(/[\s,£€$]/g, "").replace(/^[−–]/, "-");
  const pattern = negative ? /^-?(\d+\.?\d*|\.\d+)$/ : /^(\d+\.?\d*|\.\d+)$/;
  if (!pattern.test(cleaned)) return undefined;
  return new Big(cleaned);
};

/**
 * What each client spent on a stage, as the user enters it: each amount starts
 * at what is incurred. `changedBudgets` are the budgets it changes.
 */
export function useStageSpending(stage: Stage) {
  const caseContext = useCase();
  const c = caseContext.getCase();

  const parties: PartyCosts[] = (c.parties ?? [])
    .filter(({ client }) => client)
    .flatMap(({ id, name: partyName }) => {
      const budget = c.budgets?.find(({ partyId }) => partyId === id);
      if (!budget) return [];
      const locked = c.results?.lockedCosts?.[id] ?? NOTHING_LOCKED;
      return [
        {
          partyId: id,
          name: partyName,
          budget,
          locked,
          rows: plannedAt(budget, locked, stage),
        },
      ];
    });

  // what is entered as actually spent, by party and type: what is incurred
  // to begin with
  const [spent, setSpent] = useState<{ [key: string]: string }>(() =>
    Object.fromEntries(
      parties.flatMap(({ partyId, rows }) =>
        rows.map(({ type, spent }) => [`${partyId}:${type}`, spent.toString()]),
      ),
    ),
  );
  const textOf = (partyId: string, type: CostType) =>
    spent[`${partyId}:${type}`] ?? "";
  const set = (partyId: string, type: CostType, value: string) =>
    setSpent((was) => ({ ...was, [`${partyId}:${type}`]: value }));

  const validAmounts = parties.every(({ partyId, rows }) =>
    rows.every(({ type }) => amountOf(textOf(partyId, type)) !== undefined),
  );

  const changedBudgets = () =>
    parties.flatMap(({ partyId, budget, locked, rows }) => {
      let changed = budget;
      for (const row of rows) {
        const value = amountOf(textOf(partyId, row.type));
        // a type left as it was keeps its items as they are
        if (value !== undefined && !value.eq(row.spent))
          changed = withSpentAt(changed, locked, stage, row.type, value);
      }
      return changed === budget ? [] : [changed];
    });

  return { parties, textOf, set, validAmounts, changedBudgets };
}

/**
 * Closes a stage on the date it ended, and with what each client actually
 * spent on it by cost type, where the user enters it: as estimated with a
 * click, less than planned, the rest staying estimated, or more, raising the
 * budget. Nothing is entered for the user: the end date stays empty until
 * they enter it or click Today, and each amount starts at what is incurred.
 */
export const CloseStageDialog = ({
  stage,
  today,
  onClose,
}: {
  stage: Stage;
  today: IsoDate;
  onClose: () => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.timeline.closing",
  });
  const { t: tRoot } = useTranslation();
  const caseContext = useCase();
  const c = caseContext.getCase();
  const submit = useCaseSubmit();
  const name = tRoot(`home.stages.${Stage[stage]}`);
  const { parties, textOf, set, validAmounts, changedBudgets } =
    useStageSpending(stage);

  const [ended, setEnded] = useState<IsoDate>("");
  const range = endDateRange(c.timeline, caseContext.getStages(), stage, today);
  const tooEarly =
    ended !== "" && range.min !== undefined && ended < range.min;
  const validDate = ended !== "" && !tooEarly && ended <= range.max;
  const close = () => {
    const budgets = changedBudgets();
    const timeline = JSON.stringify(withEnded(c.timeline, stage, ended));
    return Promise.resolve(
      submit(
        budgets.length > 0
          ? { timeline, budgets: JSON.stringify(budgets) }
          : { timeline },
        { method: "put" },
      ),
    ).then(onClose);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("title", { stage: name })}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t("description")}</DialogContentText>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <TextField
            type="date"
            size="small"
            required
            label={t("ended")}
            value={ended}
            onChange={(event) => setEnded(event.target.value)}
            error={ended !== "" && !validDate}
            helperText={
              ended === "" || validDate
                ? undefined
                : tooEarly
                  ? tRoot("case.timeline.notBefore", {
                      date: showDate(range.min!),
                    })
                  : tRoot("case.timeline.notAfter", {
                      date: showDate(range.max),
                    })
            }
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: range.min, max: range.max },
            }}
            sx={{ width: 220 }}
          />
          <Button
            variant="outlined"
            sx={button}
            onClick={() => setEnded(today)}
          >
            {t("today")}
          </Button>
          <AsExpectedButton stage={stage} today={today} onPick={setEnded} />
        </Stack>

        {parties.map((party) => (
          <PartySpending
            key={party.partyId}
            party={party}
            stageName={name}
            textOf={(type) => textOf(party.partyId, type)}
            set={(type, value) => set(party.partyId, type, value)}
          />
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button sx={button} onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={button}
          disabled={!validDate || !validAmounts}
          onClick={close}
        >
          {t("submit", { stage: name })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * A client's open costs of the stage, what was actually spent on each type,
 * and what follows. With `final` costs, what is not spent never will be.
 */
export const PartySpending = ({
  party: { name, rows },
  stageName,
  textOf,
  set,
  final = false,
}: {
  party: PartyCosts;
  stageName: string;
  final?: boolean;
  textOf: (type: CostType) => string;
  set: (type: CostType, value: string) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.timeline.closing",
  });
  const { currency } = useCurrency();
  const money = (value: Big) => currency.format(value.toNumber());
  const figures = rows.map((row) => {
    const value = amountOf(textOf(row.type));
    return { ...row, value, diff: value?.minus(row.planned) };
  });
  const sum = (pick: (figure: (typeof figures)[number]) => Big) =>
    figures.reduce((total, figure) => total.add(pick(figure)), new Big(0));
  const zero = new Big(0);
  const raised = sum(({ diff }) => (diff?.gt(0) ? diff : zero));
  const summary: [string, Big][] = [
    [t("summary.spent"), sum(({ value, spent }) => value ?? spent)],
    [
      t(final ? "summary.notSpent" : "summary.estimated"),
      sum(({ diff }) => (diff?.lt(0) ? diff.abs() : zero)),
    ],
    [t("summary.budget"), sum(({ planned }) => planned).add(raised)],
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Stack
        direction="row"
        useFlexGap
        sx={{
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <SectionLabel component="h3">
          {t("costsOf", { name, stage: stageName })}
        </SectionLabel>
        {rows.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            sx={button}
            onClick={() =>
              rows.forEach(({ type, planned }) => set(type, planned.toString()))
            }
          >
            {t("allAsEstimated")}
          </Button>
        )}
      </Stack>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          {t("noCosts", { name })}
        </Typography>
      ) : (
        <>
          <Box sx={{ overflowX: "auto", mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("column.type")}</TableCell>
                  <TableCell align="right">{t("column.planned")}</TableCell>
                  <TableCell>{t("column.spent")}</TableCell>
                  <TableCell>{t("column.then")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {figures.map(({ type, planned, value, diff }) => (
                  <TableRow key={type}>
                    <TableCell component="th" scope="row">
                      {t(`costTypes.${type}`)}
                    </TableCell>
                    <TableCell align="right" sx={amount}>
                      {money(planned)}
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center" }}
                      >
                        <TextField
                          size="small"
                          value={textOf(type)}
                          error={value === undefined}
                          onChange={(event) => set(type, event.target.value)}
                          slotProps={{
                            htmlInput: {
                              inputMode: "decimal",
                              "aria-label": t("spentOn", {
                                type: t(`costTypes.${type}`),
                              }),
                              style: { textAlign: "right" },
                            },
                          }}
                          sx={{ width: 140 }}
                        />
                        <Button
                          size="small"
                          sx={button}
                          onClick={() => set(type, planned.toString())}
                        >
                          {t("asEstimated")}
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: diff?.gt(0) ? "warning.dark" : "text.secondary",
                        fontWeight: diff?.gt(0) ? 600 : undefined,
                      }}
                    >
                      {diff === undefined
                        ? t("invalid")
                        : diff.eq(0)
                          ? t("asPlanned")
                          : diff.lt(0)
                            ? t("less", { amount: money(diff.abs()) })
                            : t("more", { amount: money(diff) })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 2,
              mt: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "action.hover",
            }}
          >
            {summary.map(([label, figure]) => (
              <Box key={label}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {label}
                </Typography>
                <Typography sx={{ fontWeight: 600, ...amount }}>
                  {money(figure)}
                </Typography>
              </Box>
            ))}
          </Box>
          {raised.gt(0) && (
            <Typography
              variant="caption"
              component="p"
              sx={{ mt: 1, color: "warning.dark", fontWeight: 600 }}
            >
              {t("summary.raised", { amount: money(raised) })}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
